import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductsByBrand, setFilters, setPage } from '../store/slices/productSlice';
import Loader from '../components/common/loader';

const BrandProducts = () => {
  const { brand } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { brandProducts, pagination, loading, error } = useSelector(state => state.products);

  const [sortOption, setSortOption] = useState('createdAt_desc');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });

  useEffect(() => {
    // Parse query params
    const queryParams = new URLSearchParams(location.search);
    const page = parseInt(queryParams.get('page')) || 1;
    const minPrice = queryParams.get('minPrice') || '';
    const maxPrice = queryParams.get('maxPrice') || '';
    const sortValue = queryParams.get('sort') || 'createdAt_desc';
    
    setSortOption(sortValue);
    setPriceRange({ min: minPrice, max: maxPrice });
    
    // Set filters in Redux
    const [sortBy, sortOrder] = sortValue.split('_');
    dispatch(setFilters({ sortBy, sortOrder, minPrice, maxPrice }));
    dispatch(setPage(page));
    
    // Fetch products for this brand
    const params = {
      page,
      sortBy,
      sortOrder,
      minPrice: minPrice || undefined,
      maxPrice: maxPrice || undefined
    };
    
    dispatch(fetchProductsByBrand({ brand, params }));
  }, [dispatch, brand, location.search]);

  // Get formatted brand name
  const getBrandName = () => {
    return brand
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Handle sort change
  const handleSortChange = (e) => {
    const newSortOption = e.target.value;
    setSortOption(newSortOption);
    applyFilters({ sort: newSortOption });
  };

  // Handle price range change
  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setPriceRange(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters and update URL
  const applyFilters = (newFilters = {}) => {
    const queryParams = new URLSearchParams(location.search);
    
    // Update sort
    if (newFilters.sort) {
      queryParams.set('sort', newFilters.sort);
    }
    
    // Update price range
    if (newFilters.applyPrice) {
      if (priceRange.min) queryParams.set('minPrice', priceRange.min);
      else queryParams.delete('minPrice');
      
      if (priceRange.max) queryParams.set('maxPrice', priceRange.max);
      else queryParams.delete('maxPrice');
    }
    
    // Reset to page 1 when filters change
    queryParams.set('page', 1);
    
    // Update URL
    navigate(`/brand/${brand}?${queryParams.toString()}`);
  };

  // Clear all filters
  const clearFilters = () => {
    setSortOption('createdAt_desc');
    setPriceRange({ min: '', max: '' });
    navigate(`/brand/${brand}`);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('page', newPage);
    navigate(`/brand/${brand}?${queryParams.toString()}`);
  };

  // Calculate discount percentage
  const calculateDiscount = (mrp, price) => {
    if (!mrp || !price || mrp <= price) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumbs */}
      <div className="mb-6">
        <nav className="text-sm">
          <ol className="list-none p-0 inline-flex">
            <li className="flex items-center">
              <Link to="/" className="text-gray-500 hover:text-blue-600">Home</Link>
              <svg className="w-3 h-3 mx-2" fill="currentColor" viewBox="0 0 12 12">
                <path d="M9.707 5.293l-5-5a1 1 0 00-1.414 1.414L7.586 6 3.293 10.293a1 1 0 101.414 1.414l5-5a1 1 0 000-1.414z" />
              </svg>
            </li>
            <li className="flex items-center">
              <Link to="/products" className="text-gray-500 hover:text-blue-600">Products</Link>
              <svg className="w-3 h-3 mx-2" fill="currentColor" viewBox="0 0 12 12">
                <path d="M9.707 5.293l-5-5a1 1 0 00-1.414 1.414L7.586 6 3.293 10.293a1 1 0 101.414 1.414l5-5a1 1 0 000-1.414z" />
              </svg>
            </li>
            <li className="flex items-center">
              <Link to="/brands" className="text-gray-500 hover:text-blue-600">Brands</Link>
              <svg className="w-3 h-3 mx-2" fill="currentColor" viewBox="0 0 12 12">
                <path d="M9.707 5.293l-5-5a1 1 0 00-1.414 1.414L7.586 6 3.293 10.293a1 1 0 101.414 1.414l5-5a1 1 0 000-1.414z" />
              </svg>
            </li>
            <li>
              <span className="text-gray-700">{getBrandName()}</span>
            </li>
          </ol>
        </nav>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{getBrandName()}</h1>
        <div className="text-sm text-gray-500">
          {!loading.brandProducts && brandProducts[brand] && 
            `${pagination.total} product${pagination.total !== 1 ? 's' : ''}`
          }
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Filters sidebar */}
        <div className="w-full md:w-1/4 bg-white rounded-lg shadow p-4">
          <h2 className="text-xl font-semibold mb-4">Filters</h2>
          
          <div className="space-y-4">
            {/* Price range */}
            <div>
              <label className="block text-gray-700 mb-2">Price Range</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  name="min"
                  placeholder="Min"
                  value={priceRange.min}
                  onChange={handlePriceChange}
                  className="w-1/2 border rounded p-2"
                />
                <span>-</span>
                <input
                  type="number"
                  name="max"
                  placeholder="Max"
                  value={priceRange.max}
                  onChange={handlePriceChange}
                  className="w-1/2 border rounded p-2"
                />
              </div>
            </div>
            
            {/* Sort options */}
            <div>
              <label className="block text-gray-700 mb-2">Sort By</label>
              <select
                value={sortOption}
                onChange={handleSortChange}
                className="w-full border rounded p-2"
              >
                <option value="createdAt_desc">Newest First</option>
                <option value="createdAt_asc">Oldest First</option>
                <option value="sellingPrice_asc">Price: Low to High</option>
                <option value="sellingPrice_desc">Price: High to Low</option>
                <option value="discount_desc">Discount: High to Low</option>
              </select>
            </div>
            
            <div className="flex space-x-2 pt-2">
              <button
                onClick={() => applyFilters({ applyPrice: true })}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition w-full"
              >
                Apply Filters
              </button>
              <button
                onClick={clearFilters}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        
        {/* Product grid */}
        <div className="w-full md:w-3/4">
          {loading.brandProducts ? (
            <div className="flex justify-center items-center h-64">
              <Loader size="lg" />
            </div>
          ) : error.brandProducts ? (
            <div className="bg-red-100 text-red-700 p-4 rounded">
              {error.brandProducts}
            </div>
          ) : !brandProducts[brand] || brandProducts[brand].length === 0 ? (
            <div className="bg-yellow-100 text-yellow-800 p-4 rounded">
              No products found for this brand.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {brandProducts[brand].map(product => (
                  <div key={product.productId} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition">
                    <Link to={`/product/${product.productId}`}>
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={product.imageUrl} 
                          alt={product.name} 
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      
                      <div className="p-4">
                        <h3 className="font-medium text-gray-900 mb-1 truncate">
                          {product.name}
                        </h3>
                        
                        <p className="text-sm text-gray-500 mb-2">
                          {product.category}
                        </p>
                        
                        <div className="flex items-end">
                          <span className="text-lg font-bold text-gray-900">
                            ₹{product.price}
                          </span>
                          
                          {product.mrp > product.price && (
                            <>
                              <span className="text-sm text-gray-500 line-through ml-2">
                                ₹{product.mrp}
                              </span>
                              
                              <span className="text-sm text-green-600 ml-2">
                                {calculateDiscount(product.mrp, product.price)}% off
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </Link>
                    
                    <div className="px-4 pb-4">
                      <button className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition">
                        Add to Cart
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex justify-center mt-8">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className={`px-3 py-1 rounded ${
                        pagination.page === 1
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Previous
                    </button>
                    
                    {[...Array(pagination.pages)].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => handlePageChange(i + 1)}
                        className={`px-3 py-1 rounded ${
                          pagination.page === i + 1
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className={`px-3 py-1 rounded ${
                        pagination.page === pagination.pages
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandProducts;